import Check from "@mui/icons-material/Check";
import { Box, Button, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { INK } from "../../theme/tokens";
import { freeLabelColor } from "../../utils/categoricalColors";
import type { Assignment, CommandCenterNurse, FloatingNursesRecData } from "../../utils/models";
import { getEmploymentTypeDisplayName } from "../../utils/utilFunctions";
import { DepartmentChip } from "../DepartmentChips/DepartmentChips";
import { HashStringIcon } from "../HashStringIcon/HashStringIcon";
import { HeaderButton } from "../headerButton";

interface IProps {
	nurse: CommandCenterNurse;
	onSubmit: (data: FloatingNursesRecData) => void;
	onDiscard: (nurse: CommandCenterNurse) => void;
}

export const FloatingNurseItem = ({ nurse, onSubmit, onDiscard }: IProps) => {
	const [department, setDepartment] = useState<string>("");
	const [toDate, setToDate] = useState<DateTime | null>(null);
	const { getNurseFormattedName } = useHospitalData();

	const assignment: Assignment | undefined = useMemo(() => nurse?.assignments?.[nurse.assignments.length - 1], [nurse]);

	const onSubmitAssignment = (): void => {
		if (toDate) {
			setDepartment("");
			setToDate(null);
			const data: FloatingNursesRecData = {
				nurse,
				newDepartment: department,
				startDate: DateTime.now(),
				endDate: toDate,
			};

			onSubmit(data);
		}
	};

	const onDiscardAssignment = (): void => {
		setToDate(null);
		setDepartment("");
		onDiscard(nurse);
	};

	return (
		<Box
			sx={{
				p: 3,
				mt: 1,
				borderRadius: "12px",
				backgroundColor: "white",
				boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
			}}
		>
			<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
				<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "18px" }}>{getNurseFormattedName(nurse.id)}</Typography>
				<Typography
					sx={{
						color: "primary.main",
						fontSize: "15px",
						fontWeight: 500,
					}}
				>
					{nurse.role.match(/\((.*?)\)/)?.[1] || nurse.role} | {getEmploymentTypeDisplayName(nurse.employmentType)}
				</Typography>
			</Box>
			{assignment && (
				<Box>
					<Box sx={{ display: "flex", justifyContent: "space-between" }}>
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<span
								style={{
									width: 8,
									height: 8,
									borderRadius: "50%",
									backgroundColor: freeLabelColor(assignment.id).dot,
								}}
							/>
							<Typography
								sx={{
									fontWeight: 500,
									fontSize: "14px",
									color: alpha(INK.SECONDARY, 0.5),
									opacity: "50%",
									ml: 1,
								}}
							>
								{assignment.id}
							</Typography>
						</Box>
						<Typography
							sx={{
								fontWeight: 500,
								fontSize: "14px",
								color: alpha(INK.SECONDARY, 0.5),
								opacity: "50%",
							}}
						>
							{assignment.startDate.toFormat("MMM d")} - {assignment.endDate.toFormat("MMM d")}
						</Typography>
					</Box>
				</Box>
			)}
			<Divider sx={{ my: 1 }} />
			<Typography
				sx={{
					color: INK.SECONDARY,
					fontWeight: 600,
					fontSize: "13px",
					mb: 1,
				}}
			>
				Change Assignment
			</Typography>
			<Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
				{nurse.qualifiedForAssignments
					?.filter((dep: string) => dep !== assignment.id)
					.map((dep: string) => (
						<DepartmentChip
							key={dep}
							label={dep}
							className={department === dep ? "selected" : ""}
							icon={<HashStringIcon text={dep} />}
							onClick={() => setDepartment(dep)}
						/>
					))}
			</Box>
			<LocalizationProvider dateAdapter={AdapterLuxon}>
				<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
					<DatePicker
						value={toDate}
						onChange={(newValue: DateTime | null) => newValue && setToDate(newValue)}
						sx={{ width: "100%" }}
						slotProps={{
							field: {
								clearable: false,
							},
							openPickerButton: {
								color: "primary",
							},
						}}
						format="MM/dd/yyyy"
					/>
				</Box>
			</LocalizationProvider>
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					alignItems: "center",
				}}
			>
				{nurse.assignments && (
					<Button disabled={nurse.assignments?.length < 1} onClick={onDiscardAssignment}>
						Undo
					</Button>
				)}
				<HeaderButton
					onPress={onSubmitAssignment}
					state="on"
					text="Assign"
					icon={<Check style={{ fontSize: "20px" }} />}
					disabled={!department || !toDate}
					width="100px"
					secondary={false}
				/>
			</Box>
		</Box>
	);
};
